import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'bootstrap/dist/css/bootstrap.min.css';
import './chartjsSetup'

// ── Global: catch 503s and show a friendly banner/overlay ─────────────────────
// Two different 503 shapes, two different messages:
//  - Our own connectionThrottle.js "system busy" response — JSON with code: 'CAPACITY_LIMIT'.
//    Transient (seconds), small dismissible toast, no page-level interruption.
//  - Anything else 503 — most commonly Heroku's own maintenance-mode page (blocked at the
//    router, before Express ever runs, so it's plain HTML, not our JSON shape at all; see
//    middleware/connectionThrottle.js and docs on heroku maintenance:on). Treated as "the app
//    is down for a bit" — full-screen overlay, auto-recovers once the API is reachable again.
let capacityToastTimer = null;
let maintenanceOverlayShown = false;

axios.interceptors.response.use(
  res => res,
  err => {
    if (err?.response?.status === 503) {
      if (err.response.data?.code === 'CAPACITY_LIMIT') {
        showCapacityBanner(err.response.data.message);
      } else {
        showMaintenanceOverlay();
      }
    }
    return Promise.reject(err);
  }
);

function showCapacityBanner(message) {
  const existing = document.getElementById('capacity-limit-banner');
  if (existing) {
    clearTimeout(capacityToastTimer);
  } else {
    const banner = document.createElement('div');
    banner.id = 'capacity-limit-banner';
    banner.setAttribute('role', 'alert');
    banner.style.cssText = [
      'position:fixed', 'bottom:24px', 'left:50%', 'transform:translateX(-50%)',
      'z-index:9999', 'min-width:320px', 'max-width:480px',
      'background:#fff3cd', 'color:#664d03',
      'border:1px solid #ffecb5', 'border-radius:8px',
      'padding:14px 18px', 'box-shadow:0 4px 16px rgba(0,0,0,.15)',
      'display:flex', 'align-items:flex-start', 'gap:12px', 'font-size:14px',
    ].join(';');
    banner.innerHTML = `
      <span style="font-size:20px;line-height:1">⏳</span>
      <div style="flex:1">
        <strong style="display:block;margin-bottom:2px">System busy</strong>
        ${message}
      </div>
      <button onclick="document.getElementById('capacity-limit-banner').remove()"
        style="background:none;border:none;font-size:18px;line-height:1;cursor:pointer;color:#664d03;padding:0 0 0 4px"
        aria-label="Dismiss">×</button>
    `;
    document.body.appendChild(banner);
  }
  // Auto-dismiss after 6 seconds
  capacityToastTimer = setTimeout(() => {
    const el = document.getElementById('capacity-limit-banner');
    if (el) el.remove();
  }, 6000);
}

function showMaintenanceOverlay() {
  if (maintenanceOverlayShown) return;
  maintenanceOverlayShown = true;

  const overlay = document.createElement('div');
  overlay.id = 'maintenance-overlay';
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:99999',
    'background:#1b2638', 'color:#fff',
    'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
    'text-align:center', 'padding:32px', 'font-family:inherit',
  ].join(';');
  overlay.innerHTML = `
    <div style="font-size:56px;line-height:1;margin-bottom:16px">🍕</div>
    <div style="font-size:22px;font-weight:700;margin-bottom:8px">We're baking something warm.</div>
    <div style="font-size:15px;color:#c3cadb;max-width:420px">
      quickLOG is down for a quick update — we'll be back shortly. This page will refresh
      automatically as soon as we're back.
    </div>
  `;
  document.body.appendChild(overlay);

  // Poll the lightweight health endpoint — it's blocked by Heroku maintenance mode the same as
  // every other route, so it only succeeds once maintenance mode is actually lifted.
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const poll = setInterval(() => {
    fetch(`${apiUrl}/health`)
      .then(res => { if (res.ok) { clearInterval(poll); window.location.reload(); } })
      .catch(() => {}); // still down — keep polling
  }, 10000);
}


// The employee/driver portal is a quickLOG-platform tool, not customer-facing — it should always
// show quickLOG's own "qL" branding (logo192.png/favicon set in public/index.html), never a
// tenant's own logo/manifest injected below for the rest of this (tenant-branded) deployment.
const isPortalRoute = window.location.pathname.startsWith('/portal');

if (process.env.REACT_APP_FAVICON_APPLE && !isPortalRoute) {
  const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
  if (appleIcon) {
    appleIcon.href = process.env.REACT_APP_FAVICON_APPLE;
  }
}

if (process.env.REACT_APP_FAVICON_32 && !isPortalRoute) {
  const favicon32 = document.querySelector("link[rel='icon'][sizes='32x32']");
  if (favicon32) {
    favicon32.href = process.env.REACT_APP_FAVICON_32;
  }
}

if (process.env.REACT_APP_FAVICON_16 && !isPortalRoute) {
  const favicon16 = document.querySelector("link[rel='icon'][sizes='16x16']");
  if (favicon16) {
    favicon16.href = process.env.REACT_APP_FAVICON_16;
  }
}

if (process.env.REACT_APP_SITE_WEBMANIFEST && !isPortalRoute) {
  const manifest = document.querySelector("link[rel='manifest']");
  if (manifest) {
    manifest.href = process.env.REACT_APP_SITE_WEBMANIFEST;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
// React.StrictMode removed: react-quill v2 uses findDOMNode internally,
// which is incompatible with StrictMode in React 18 and causes a noisy
// console warning. Remove StrictMode until react-quill ships a fix.
root.render(
  <App />
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
